import { requiresAiSharingConsent } from '@/lib/appDistribution';
import React, { Suspense, useEffect, useState } from 'react';
import { ActivityIndicator, Linking, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuthStore } from '@/store/auth';
import { useAiSharingConsent } from '@/store/aiSharingConsent';
import { useNativeAnalyticsConsent } from '@/store/nativeAnalyticsConsent';
import { useAuthHydrated } from '@/hooks/useRequireAuth';
import { ThemeProvider } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { ModalBase } from '@/components/Modal/ModalRoot';
import AiSharingConsentDialog from './AiSharingConsentDialog';

const DeleteAccountModal = React.lazy(() => import('@/components/Modal/DeleteAccountModal'));

export function useMobileAiSharingAllowed() {
  const userId = useAuthStore(state => state.user?.id);
  const token = useAuthStore(state => state.token);
  const allowed = useAiSharingConsent(state => !state.loading && state.allowed && state.accountId === userId);
  return !requiresAiSharingConsent() || !!(token && userId && allowed);
}

function ConsentScreen({ accountId }) {
  const [declined, setDeclined] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const choose = useAiSharingConsent(state => state.choose);
  const analyticsAllowed = useNativeAnalyticsConsent(state => state.allowed);
  useEffect(() => { if (Platform.OS === 'ios') useNativeAnalyticsConsent.getState().load(); }, []);
  const decide = async allowed => {
    if (saving || useAuthStore.getState().user?.id !== accountId) return;
    setSaving(true); setError('');
    try { await choose(accountId, allowed); if (!allowed) setDeclined(true); }
    catch (_) { setError('Your choice could not be saved. Please try again.'); }
    finally { setSaving(false); }
  };
  const link = url => Linking.openURL(url).catch(() => setError('Unable to open this link. Please try again.'));
  const action = (label, onPress, primary = false) => <Pressable accessibilityRole="button" accessibilityLabel={label} disabled={saving} onPress={onPress} style={[styles.button, primary && styles.primary, saving && { opacity: 0.6 }]}><Text style={[styles.buttonText, primary && { color: '#FFFFFF' }]}>{label}</Text></Pressable>;

  return <ThemeProvider forceTheme="white" scoped><SafeAreaView style={styles.surface}>
    <ScrollView contentContainerStyle={styles.scroll}>
      <View style={styles.card}>
        <Text style={styles.brand}>Gab AI</Text>
        <Text accessibilityRole="header" style={styles.title}>{declined ? 'Your account' : 'AI that works. For you.'}</Text>
        {declined && <Text style={styles.body}>AI stays off until you agree. You can manage your account below.</Text>}
        {declined && action('Review AI data sharing', () => setDeclined(false), true)}
        {declined && Platform.OS === 'ios' && analyticsAllowed && action('Turn off usage analytics', async () => {
          setError('');
          try { await useNativeAnalyticsConsent.getState().choose(false); }
          catch (_) { setError('Analytics are off for this session. Unable to save your choice; please try again.'); }
        })}
        {declined && action('Manage subscription', () => link(Platform.OS !== 'android' ? 'https://apps.apple.com/account/subscriptions' : 'https://play.google.com/store/account/subscriptions'))}
        {declined && action('Delete account', () => setDeleting(true))}
        {declined && action('Sign out', () => useAuthStore.getState().clearSession())}
        {declined && !!error && <Text accessibilityRole="alert" style={styles.error}>{error}</Text>}
      </View>
    </ScrollView>
    <AiSharingConsentDialog visible={!declined} busy={saving} error={error} onAllow={() => decide(true)} onAccountOptions={() => decide(false)} onPrivacy={() => link('https://gab.ai/privacy-policy')} />
    {deleting && <ModalBase isOpen onClose={() => setDeleting(false)} width={520} height={780} heightMobile="96%"><Suspense fallback={<ActivityIndicator />}><DeleteAccountModal onClose={() => setDeleting(false)} /></Suspense></ModalBase>}
  </SafeAreaView></ThemeProvider>;
}

export default function AiSharingConsentGate({ children }) {
  const hydrated = useAuthHydrated();
  const token = useAuthStore(state => state.token);
  const userId = useAuthStore(state => state.user?.id);
  const state = useAiSharingConsent();
  const mobile = requiresAiSharingConsent();
  useEffect(() => {
    if (mobile && hydrated && token && userId && state.accountId !== userId) void state.load(userId);
  }, [mobile, hydrated, !!token, userId, state.accountId, state.load]);
  if (!mobile || (hydrated && !token)) return children;
  if (!hydrated || !userId || state.accountId !== userId || state.loading) return <View style={styles.loading}><ActivityIndicator accessibilityLabel="Loading your privacy choice" /></View>;
  if (!state.allowed) return <ConsentScreen key={userId} accountId={userId} />;
  return children;
}

const styles = StyleSheet.create({
  surface: { flex: 1, backgroundColor: '#F5F5F7' },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { width: '100%', maxWidth: 540, gap: 14, paddingVertical: 20 },
  brand: { color: '#6E6E73', fontSize: 17, fontWeight: '600' },
  title: { color: '#1D1D1F', fontSize: 32, lineHeight: 38, fontWeight: '700' },
  body: { color: '#1D1D1F', fontSize: 15, lineHeight: 22 },
  button: { minHeight: 48, borderRadius: 14, padding: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF' },
  primary: { backgroundColor: '#1D1D1F' },
  buttonText: { color: '#1D1D1F', fontSize: 15, fontWeight: '600' },
  error: { color: '#B42318', fontSize: 14, lineHeight: 20 },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF' },
});
